import React, { useState, useEffect, useCallback, useMemo } from "react";
import { Link, useLocation } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import {
  ChevronDown,
  ChevronUp,
  Folder,
  Store,
  Truck,
  DollarSign,
  ShieldCheck,
  Settings,
  BarChart,
} from "lucide-react";
import { toast } from "sonner";
import { getSubsistema, useAuth } from "../../hooks/AuthenticateContext";
import { carrierMenuItems, isCarrierUser, menuItems } from "../../utils/menuItems";
import { validPaths } from "../../routes/validPaths";

interface SidebarProps {
  isOpen: boolean;
  isMobile?: boolean;
  onClose?: () => void;
}

const iconMap: { [key: string]: React.ElementType } = {
  Folder: Folder,
  Store: Store,
  Truck: Truck,
  DollarSign: DollarSign,
  ShieldCheck: ShieldCheck,
  Settings: Settings,
  BarChart: BarChart,
  };

const Sidebar: React.FC<SidebarProps> = ({ isOpen, isMobile = false, onClose }) => {
  const { user, isLoadingPermissions } = useAuth();
  const [permissions, setPermissions] = useState<any[]>([]);
  const [expandedMenu, setExpandedMenu] = useState<string | null>(null);
  const location = useLocation();

  useEffect(() => {
    const storedPermissions = localStorage.getItem("permissions") || localStorage.getItem("permissions");
    if (storedPermissions) {
      setPermissions(JSON.parse(storedPermissions));
    }
  }, [user, isLoadingPermissions]);

  // As chaves de permissão (ex.: "carrossel") são compartilhadas entre o menu
  // do pátio e o da transportadora; só contam as permissões do subsistema do
  // login (linhas sem subsistema definido continuam valendo).
  const subsistemaAtual = getSubsistema(user);

  const hasPermission = useCallback((permissionKey?: string) => {
    if (user?.grupo === "ADMINISTRADOR") return true;
    if (!permissionKey) return true;
    // Se o usuário for admin ou não tiver permissões carregadas, talvez decidir política default.
    // Assumindo que se não tem permissões explícitas e precisa de uma, nega. (Exceto se lista vazia significar outra coisa)
    if (permissions.length === 0) {
        // Fallback: se não houver lista de permissões mas o usuário estiver logado,
        // talvez deva permitir ou bloquear.
        // Assumindo blocking by default se a key existe.
        return false;
    }

    return permissions.some((p: any) =>
      (p.submodule === permissionKey || p.module === permissionKey) &&
      p.action === 'ACESSAR' &&
      (!p.subsistema || p.subsistema === subsistemaAtual)
    );
  }, [permissions, user, subsistemaAtual]);

  const navMenuItems = useMemo(() => {
    const items = isCarrierUser(user) ? carrierMenuItems : menuItems;

    return items.map(item => {
        // Verifica permissão do item pai (caso exista)
        if (item.permission && !hasPermission(item.permission)) {
            return null;
        }

        // Se tiver submenu, filtra os filhos
        if (item.submenu) {
            const filteredSubmenu = item.submenu.filter(subItem => 
                hasPermission(subItem.permission)
            );

            // Se após filtrar não sobrar nada, não mostra o pai (a menos que o pai tenha link próprio e permissão)
            if (filteredSubmenu.length === 0 && !item.path) {
                return null;
            }

            return { ...item, submenu: filteredSubmenu };
        }

        return item;
    }).filter(Boolean) as typeof items;
  }, [user, permissions, hasPermission]);

  const toggleSubMenu = (title: string) => {
    if (expandedMenu === title) {
      setExpandedMenu(null);
    } else {
      setExpandedMenu(title);
    }
  };

  const handleNavigation = (e: React.MouseEvent, path?: string) => {
    if (!path || path === "#") return;

    if (!validPaths.includes(path)) {
      e.preventDefault();
      toast.warning("Tela em desenvolvimento");
      return;
    }

    if (isMobile) {
      onClose?.();
    }
  };

  return (
    <div
      className={`bg-[#003366] text-white h-full transition-all duration-300 flex flex-col shrink-0 ${
        isMobile
          ? `fixed left-0 top-16 bottom-0 z-40 w-[min(18rem,85vw)] transform shadow-2xl ${isOpen ? "translate-x-0" : "-translate-x-full"}`
          : isOpen ? "w-72" : "w-0 overflow-hidden"
        }`}
    >
      <div className="flex-1 overflow-y-auto py-4">
        <ul>
          {navMenuItems.map((item) => {
            const isExpanded = expandedMenu === item.title;
            const IconComponent = item.icon ? iconMap[item.icon] : null;
            const isActive = isExpanded || (item.path && location.pathname === item.path);

            return (
              <li key={item.title}>
                <div
                  className={`flex items-center px-6 py-4 cursor-pointer transition-colors relative ${isActive ? "" : "hover:bg-[#004080]"
                    }`}
                  onClick={() => item.submenu ? toggleSubMenu(item.title) : null}
                >
                  {isActive && (
                    <div className="absolute left-0 top-0 bottom-0 w-1 bg-[#FFCC00] rounded-r-sm"></div>
                  )}

                  <Link
                    to={item.path || "#"}
                    className="flex items-center w-full"
                    onClick={(e) => {
                      if (item.submenu) {
                        e.preventDefault();
                      } else {
                        handleNavigation(e, item.path);
                      }
                    }}
                  >
                    {IconComponent && (
                      <IconComponent
                        size={24}
                        className={`mr-4 ${isActive ? "text-[#FFCC00]" : "text-gray-400"}`}
                      />
                    )}
                    {isOpen && (
                      <span className={`font-medium flex-1 text-base ${isActive ? "text-white" : "text-gray-300"}`}>
                        {item.title}
                      </span>
                    )}
                    {isOpen && item.submenu && (
                      <span className="ml-auto text-gray-400">
                        {isExpanded ? <ChevronUp size={20} /> : <ChevronDown size={20} />}
                      </span>
                    )}
                  </Link>
                </div>

                <AnimatePresence>
                  {isOpen && isExpanded && item.submenu && (
                    <motion.ul
                      initial={{ height: 0, opacity: 0 }}
                      animate={{ height: "auto", opacity: 1 }}
                      exit={{ height: 0, opacity: 0 }}
                      className="overflow-hidden bg-[#003366] relative ml-8 border-l border-gray-600"
                    >
                      {item.submenu.map((subItem) => {
                        const isSubActive = location.pathname === subItem.path;
                        return (
                          <li key={subItem.title} className="relative">
                            <Link
                              to={subItem.path}
                              className={`flex items-center px-6 py-3 text-base transition-colors ${isSubActive ? "text-white" : "text-gray-400 hover:text-white"
                                }`}
                              onClick={(e) => handleNavigation(e, subItem.path)}
                            >
                              <div className={`w-1.5 h-1.5 rounded-full mr-3 ${isSubActive ? "bg-[#FFCC00]" : "bg-gray-500"}`}></div>
                              {subItem.title}
                            </Link>
                          </li>
                        );
                      })}
                    </motion.ul>
                  )}
                </AnimatePresence>
              </li>
            );
          })}
        </ul>
      </div>
    </div>
  );
};

export default Sidebar;
